import { readPref, writePref } from '~/composables/usePersistedPref'
/**
 * Panel layout state management.
 *
 * Layout zones:
 *   [Rail] │ [Docs drawer?] [Center content OR Chat] [Chat panel?] │ [Context rail]
 *
 * The two rails sit on the frame's ground either side of the light region and share one pair of
 * widths (w-56 expanded / w-12 collapsed).
 *
 * - Chat fills the center when no content is active.
 * - Chat slides to a fixed-width right panel when content is showing.
 * - Documents drawer toggles on/off, pushing center content.
 *
 * Below `sm` none of that fits, so the shell collapses to ONE pane plus a bottom tab bar and both
 * rails hide — see `useMobilePane` below and `app/components/shell/MobileTabBar.vue`.
 */

export type MobilePane = 'chat' | 'docs' | 'content'

/**
 * The chat-home routes, where chat owns the whole center and there is no page to show.
 *
 * A plain function rather than a composable so it carries no Nuxt-context requirement: it is the ONE
 * derivation of "is a page showing", and both the shell and the mobile tab bar have to agree on it.
 */
export function isContentRoute(path: string): boolean {
  return !(path === '/app' || path === '/app/' || path.startsWith('/app/c/'))
}

/**
 * Whether the chat pane has a DOM node to show at all (#869).
 *
 * `chatPanelOpen` is DESKTOP-only state — whether the floating chat popup is open — but it is what
 * `PanelLayout.vue` gated the whole chat container on, `v-if`'d rather than `v-show`'d. On a phone
 * in single-pane mode, tapping the compact header's "X" (reachable there too, before #869) flips
 * `chatPanelOpen` to `false` and persists it; the mobile tab bar's "Chat" button only ever set
 * `mobilePane` and never touched that flag, so the container's DOM node was simply gone and nothing
 * in the tab bar could bring it back. `mobilePane === 'chat'` must therefore be enough **on its
 * own** to keep the container mounted, regardless of the desktop flag's persisted value.
 *
 * A plain function, like `isContentRoute` above, so both the shell and a test can call it without a
 * Nuxt context.
 */
export function isChatPaneVisible(contentActive: boolean, chatPanelOpen: boolean, mobilePane: MobilePane): boolean {
  return !contentActive || chatPanelOpen || mobilePane === 'chat'
}

/**
 * Which single pane is on screen below `sm`, where the columns stop fitting side by side (MAR-123).
 *
 * **Deliberately not persisted.** The desktop panel booleans are a preference; this is "where am I
 * right now", and restoring it a day later would drop someone into the documents pane with no memory
 * of having opened it. It defaults to the pane the route implies and is overridden only by an
 * explicit tap in the tab bar — `PanelLayout` clears the override on every navigation, so a route
 * change always lands on the page the user just asked for.
 *
 * Above `sm` nothing here has any effect: the columns carry `sm:` classes that show them regardless.
 */
export function useMobilePane() {
  const route = useRoute()
  const { openDocsDrawer } = usePanelLayout()
  const override = useState<MobilePane | null>('panel-mobile-pane', () => null)

  const contentActive = computed(() => isContentRoute(route.path))
  const pane = computed<MobilePane>(() => override.value ?? (contentActive.value ? 'content' : 'chat'))

  /** Documents is a column gated on `docsDrawerOpen`, so showing it has to open it too — otherwise
   *  the tab would set a pane whose column is not in the DOM. */
  function show(next: MobilePane) {
    if (next === 'docs') openDocsDrawer()
    override.value = next
  }

  function reset() {
    override.value = null
  }

  return { pane, contentActive, show, reset }
}

/** The four positions the floating chat can dock to inside the light region. */
export type ChatCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
export const CHAT_CORNERS: ChatCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right']

/** Multi-state navigation sidebar geometry & timing constants (MAR-1337). */
export const SIDEBAR_DEFAULT_WIDTH = 240
export const SIDEBAR_MIN_WIDTH = 200
export const SIDEBAR_MAX_WIDTH = 360
export const SIDEBAR_COLLAPSE_THRESHOLD = 120
export const SIDEBAR_PEEK_GRACE_MS = 100
export const SIDEBAR_PEEK_ZONE_PX = 16

export function clampSidebarWidth(px: number): number {
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(px)))
}

let peekDismissTimer: ReturnType<typeof setTimeout> | null = null
let sidebarWidthBeforeResize: number | null = null

export function usePanelLayout() {
  const docsDrawerOpen = useState<boolean>('panel-docs', () => false)
  const sidebarWidth = useState<number>('panel-sidebar-width', () => SIDEBAR_DEFAULT_WIDTH)
  const isSidebarCollapsed = useState<boolean>('panel-sidebar-collapsed', () => false)
  const isSidebarPeeking = useState<boolean>('panel-sidebar-peeking', () => false)
  const isSidebarResizing = useState<boolean>('panel-sidebar-resizing', () => false)
  const peekSuppressed = useState<boolean>('panel-sidebar-peek-suppressed', () => false)
  /** Backward-compatible computed alias: true whenever the sidebar is docked or peeking. */
  const railExpanded = computed(() => !isSidebarCollapsed.value || isSidebarPeeking.value)
  const chatPanelOpen = useState<boolean>('panel-chat', () => true)
  /**
   * The context rail on the right (MAR-115, reshaped by MAR-128).
   *
   * It has THREE states, not two, and only this boolean is persisted:
   *
   *   hidden    — the scope is global. Owned by `webChat.activeScope`, not by this flag.
   *   collapsed — scoped, `false`. An initials chip plus one glyph per section with its count.
   *   expanded  — scoped, `true`.
   *
   * Hidden used to be impossible: the rail was always present, and collapsing it to an initials chip
   * was justified by "a rail that vanished would take the *which space am I in* answer with it". That
   * holds only while there IS a space — with no scope there is no answer to lose, and the rail was
   * rendering an empty state nobody asked for. The chip survives for the scoped case, which is the
   * one it was actually arguing for.
   */
  const contextRailExpanded = useState<boolean>('panel-context', () => false)
  /**
   * Which corner of the light region the floating chat is docked to (MAR-125).
   *
   * A corner rather than a free x/y: the popup has to stay inside the shell and stay clear of the
   * content beneath it, and an arbitrary position can end up half-off a smaller viewport or parked
   * over the thing the user is reading. Four corners are also what a user can re-find without
   * looking. Persisted with the other panel state, so it survives a reload like any other preference.
   */
  const chatCorner = useState<ChatCorner>('panel-chat-corner', () => 'bottom-right')

  // ── Law view request (citation → center content) ──
  interface LawRequest {
    normId: string | null
    sectionId: string
    // Back-reference to the chat message a citation came from (A5). Set only when the caller is a chat
    // citation; the laws page and other callers omit it, so the "return to answer" affordance shows up
    // exclusively for citations opened from the transcript. `conversationId` lets the full-page law
    // view ("Ver ley completa") link back to the conversation.
    source?: { messageId: string; conversationId?: string | null }
  }
  const lawRequest = useState<LawRequest | null>('panel-law', () => null)

  // ── Highlight request (agent → viewer in center) ──
  interface HighlightRequest {
    text: string
    page?: number
    label?: string
  }
  const highlightRequest = useState<HighlightRequest | null>('panel-highlight', () => null)

  // ── Sheet command request (companion silent tool → open sheet view) ──
  // The sheet view, when open, watches this and applies the filter / row selection.
  // Mirrors highlightRequest: a one-shot command channel across components.
  interface SheetCommandRequest {
    type: 'filter' | 'select'
    query?: string
    ids?: string[]
  }
  const sheetCommandRequest = useState<SheetCommandRequest | null>('panel-sheet-cmd', () => null)

  // ── Content active (derived) ──
  // True when something is showing in the center that isn't chat.
  // Checked by the layout to decide if chat is centered or in the right panel.
  // This is evaluated in the layout component where route and activeItemId are available.

  function toggleDocsDrawer() {
    docsDrawerOpen.value = !docsDrawerOpen.value
    persist()
  }

  /** Idempotent open — used when something needs to be SEEN (e.g. files dropped into chat start
   *  indexing, and the queue that reports it lives in this drawer). Never closes. */
  function openDocsDrawer() {
    if (docsDrawerOpen.value) return
    docsDrawerOpen.value = true
    persist()
  }

  function toggleChatPanel() {
    chatPanelOpen.value = !chatPanelOpen.value
    persist()
  }

  function setChatCorner(corner: ChatCorner) {
    if (chatCorner.value === corner) return
    chatCorner.value = corner
    persist()
  }

  function cancelDismissSidebarPeek() {
    if (peekDismissTimer) {
      clearTimeout(peekDismissTimer)
      peekDismissTimer = null
    }
  }

  function setSidebarWidth(px: number) {
    sidebarWidth.value = clampSidebarWidth(px)
    persist()
  }

  function setSidebarResizing(resizing: boolean) {
    if (resizing && !isSidebarResizing.value) {
      sidebarWidthBeforeResize = sidebarWidth.value
    } else if (!resizing) {
      sidebarWidthBeforeResize = null
    }
    isSidebarResizing.value = resizing
  }

  function collapseSidebar(suppressPeek = true) {
    cancelDismissSidebarPeek()
    sidebarWidthBeforeResize = null
    isSidebarResizing.value = false
    isSidebarCollapsed.value = true
    isSidebarPeeking.value = false
    peekSuppressed.value = suppressPeek
    persist()
  }

  function dockSidebar() {
    cancelDismissSidebarPeek()
    sidebarWidthBeforeResize = null
    isSidebarResizing.value = false
    isSidebarCollapsed.value = false
    isSidebarPeeking.value = false
    peekSuppressed.value = false
    persist()
  }

  /**
   * Real-time resize handler for the 6px right-edge handle (MAR-1337).
   * - Dragging left past 120px (`SIDEBAR_COLLAPSE_THRESHOLD`) snaps the sidebar to 0px collapsed
   *   and sets `peekSuppressed = true` while preserving the pre-drag `sidebarWidth` (`[200, 360]`).
   * - Otherwise clamps `sidebarWidth` to `[200, 360]`.
   */
  function handleSidebarDrag(rawWidth: number): 'resized' | 'collapsed' {
    if (rawWidth < SIDEBAR_COLLAPSE_THRESHOLD) {
      if (sidebarWidthBeforeResize !== null) {
        sidebarWidth.value = clampSidebarWidth(sidebarWidthBeforeResize)
        sidebarWidthBeforeResize = null
      }
      collapseSidebar(true)
      return 'collapsed'
    }
    sidebarWidth.value = clampSidebarWidth(rawWidth)
    persist()
    return 'resized'
  }

  function startSidebarPeek() {
    if (!isSidebarCollapsed.value || peekSuppressed.value) return
    cancelDismissSidebarPeek()
    isSidebarPeeking.value = true
  }

  function scheduleDismissSidebarPeek() {
    if (!isSidebarCollapsed.value || !isSidebarPeeking.value) return
    cancelDismissSidebarPeek()
    peekDismissTimer = setTimeout(() => {
      peekDismissTimer = null
      isSidebarPeeking.value = false
    }, SIDEBAR_PEEK_GRACE_MS)
  }

  function clearPeekSuppressionIfOutside(clientX: number) {
    if (peekSuppressed.value && clientX > SIDEBAR_PEEK_ZONE_PX) {
      peekSuppressed.value = false
    }
  }

  function toggleRail() {
    if (isSidebarCollapsed.value) {
      dockSidebar()
    } else {
      collapseSidebar(true)
    }
  }

  function toggleContextRail() {
    // A user's own toggle always wins — including over a content claim, which is why the claims are
    // cleared here rather than merely ignored.
    wideClaims.value = []
    contextRailExpanded.value = !contextRailExpanded.value
    persist()
  }

  /**
   * Content that needs the width (MAR-179 follow-up).
   *
   * A page whose main surface cannot fit — the batch review table is 21 columns and ~3000px — can
   * ask the context rail to fall back to its collapsed strip. It is a SUPPRESSION, not a preference:
   * nothing is persisted, so the user's own setting is untouched and returns the moment the claim is
   * released. The rail is never hidden outright, only narrowed: it answers "what can Marcus see",
   * and a batch run is precisely when the active scope matters most.
   *
   * A stack of ids rather than a boolean, so two claimants cannot un-collapse each other on unmount.
   * Claims are made by MOUNTING `<ShellWideContentClaim>`, which is Vue's own reactive edge — no
   * watcher, and it unwinds correctly on route change, on mode change and on unmount.
   */
  const wideClaims = useState<string[]>('panel-wide-claims', () => [])
  const contextRailSuppressed = computed(() => wideClaims.value.length > 0)
  function claimWideContent(id: string) {
    if (!wideClaims.value.includes(id)) wideClaims.value = [...wideClaims.value, id]
  }
  function releaseWideContent(id: string) {
    wideClaims.value = wideClaims.value.filter(x => x !== id)
  }

  /**
   * Force the context rail open, overriding the persisted preference — called from `setScope` when a
   * client or case is SELECTED (MAR-128).
   *
   * Picking a space is a request to see it. Honouring a stale `false` there means the user chooses a
   * case and gets a 48px strip sliding in, which buries the thing they just asked for. It re-fires on
   * every selection, including re-scoping while already scoped, so the rail is never showing space A
   * collapsed while the user selects space B.
   *
   * It persists, so the forced state is also the new preference — and the user's own collapse right
   * afterwards persists over it in turn. **The remembered-scope restore is deliberately NOT a
   * selection** and must never reach here: it goes through `applyRememberedScope`, which assigns
   * `activeScope` directly, so a cold start with a remembered case honours the saved preference
   * rather than springing the rail open on every load.
   */
  function revealContextRail() {
    if (contextRailExpanded.value) return
    contextRailExpanded.value = true
    persist()
  }

  function openLawSection(
    normId: string | null | undefined,
    sectionId: string,
    source?: { messageId: string; conversationId?: string | null },
  ) {
    // Mutually exclusive with the document-preview panel — both occupy the same slot in
    // `ChatPanel.vue`. That panel is conversation state owned by the `web-chat` store (MAR-437
    // follow-up), not shell state, so clearing it means reaching into that store rather than a local
    // ref here.
    useWebChatStore().closeDocPreview()
    lawRequest.value = { normId: normId || null, sectionId, ...(source ? { source } : {}) }
  }

  function closeLaw() {
    lawRequest.value = null
  }

  function requestHighlight(req: HighlightRequest) {
    highlightRequest.value = req
  }

  function clearHighlight() {
    highlightRequest.value = null
  }

  function requestSheetFilter(query: string) {
    sheetCommandRequest.value = { type: 'filter', query }
  }

  function requestSheetSelect(ids: string[]) {
    sheetCommandRequest.value = { type: 'select', ids }
  }

  function clearSheetCommand() {
    sheetCommandRequest.value = null
  }

  /** Close whatever content is in the center and return to chat-centered state. */
  function closeContent() {
    lawRequest.value = null
    if (import.meta.client) {
      const route = useRoute()
      const isHome = route.path === '/app' || route.path === '/app/'
      if (!isHome) {
        navigateTo('/app')
      }
    }
  }

  function persist() {
    writePref('marcus:panels', JSON.stringify({
      docs: docsDrawerOpen.value,
      rail: !isSidebarCollapsed.value,
      sidebarWidth: sidebarWidth.value,
      isSidebarCollapsed: isSidebarCollapsed.value,
      chat: chatPanelOpen.value,
      context: contextRailExpanded.value,
      chatCorner: chatCorner.value,
    }))
  }

  function loadState() {
    try {
      const saved = readPref('marcus:panels')
      if (saved) {
        const state = JSON.parse(saved)
        docsDrawerOpen.value = state.docs ?? false
        if (typeof state.sidebarWidth === 'number' && Number.isFinite(state.sidebarWidth)) {
          sidebarWidth.value = clampSidebarWidth(state.sidebarWidth)
        }
        if (typeof state.isSidebarCollapsed === 'boolean') {
          isSidebarCollapsed.value = state.isSidebarCollapsed
        } else if (typeof state.rail === 'boolean') {
          isSidebarCollapsed.value = !state.rail
        }
        isSidebarPeeking.value = false
        peekSuppressed.value = false
        chatPanelOpen.value = state.chat ?? true
        contextRailExpanded.value = state.context ?? false
        if (CHAT_CORNERS.includes(state.chatCorner)) chatCorner.value = state.chatCorner
      }
    } catch { /* ignore */ }
  }

  return {
    docsDrawerOpen,
    sidebarWidth,
    isSidebarCollapsed,
    isSidebarPeeking,
    isSidebarResizing,
    peekSuppressed,
    railExpanded,
    chatPanelOpen,
    chatCorner,
    setChatCorner,
    contextRailExpanded,
    contextRailSuppressed,
    claimWideContent,
    releaseWideContent,
    lawRequest,
    highlightRequest,
    sheetCommandRequest,
    toggleDocsDrawer,
    openDocsDrawer,
    toggleChatPanel,
    setSidebarWidth,
    setSidebarResizing,
    collapseSidebar,
    dockSidebar,
    handleSidebarDrag,
    startSidebarPeek,
    scheduleDismissSidebarPeek,
    cancelDismissSidebarPeek,
    clearPeekSuppressionIfOutside,
    toggleRail,
    toggleContextRail,
    revealContextRail,
    openLawSection,
    closeLaw,
    requestHighlight,
    clearHighlight,
    requestSheetFilter,
    requestSheetSelect,
    clearSheetCommand,
    closeContent,
    loadState,
  }
}
